import { useMemo, useState } from "react";
import {
  AlignLeft,
  ArrowDown,
  ArrowUp,
  BarChart3,
  Box,
  ChevronDown,
  ClipboardList,
  Columns3,
  Copy,
  GalleryHorizontalEnd,
  HelpCircle,
  Image as ImageIcon,
  LayoutGrid,
  Loader2,
  Megaphone,
  MousePointerClick,
  Palette,
  PanelBottom,
  PanelTop,
  PlaySquare,
  Plus,
  Quote,
  RectangleHorizontal,
  Search,
  Sparkles,
  Tags,
  Trash2,
  Type,
  Ungroup,
  type LucideIcon,
} from "lucide-react";

import { registry, type RegistryName } from "@dynamic-landing/components";
import { aiAssist } from "@/data/services";
import { FormFieldsEditor, FormSubmissionsButton } from "./FormBuilder";
import {
  BLOCK_ALIGN_VALUES,
  BLOCK_WIDTH_VALUES,
  COL_SPAN_VALUES,
  isLayoutParentName,
  ROW_SPAN_VALUES,
} from "@dynamic-landing/layout";
import { Card } from "@/ui";
import { cn } from "@/lib/cn";
import { describeSchema, type FieldDescriptor, type SchemaLike } from "@/editor/schemaIntrospect";
import type { ComponentNode } from "@/editor/types";
import { COMPONENT_NAMES } from "./useBuilder";

/**
 * The inspector: hand-edit the selected block's props (typed inputs from the
 * component's schema), reorder it, or delete it. This is the manual half of the
 * AI-first hybrid — Nara proposes, you also edit directly.
 */
export function Inspector({
  node,
  rootNodeId,
  parentName,
  onChange,
  onMove,
  onRemove,
  onDuplicate,
  onUnwrap,
}: {
  node: ComponentNode | null;
  rootNodeId: string | undefined;
  /** Component name of the selected node's PARENT — used to surface grid
   *  placement (colSpan/rowSpan) controls when the parent is a Grid/Columns. */
  parentName?: string;
  onChange: (nodeId: string, prop: string, value: unknown) => void;
  onMove: (nodeId: string, delta: -1 | 1) => void;
  onRemove: (nodeId: string) => void;
  /** Duplicate the node as the next sibling (optional). */
  onDuplicate?: (nodeId: string) => void;
  /** Ungroup a container: promote its children into its parent (optional). */
  onUnwrap?: (nodeId: string) => void;
}) {
  if (!node) {
    return (
      <Card className="grid place-items-center px-5 py-8 text-center">
        <MousePointerClick className="h-5 w-5 text-subtle" strokeWidth={1.5} />
        <p className="mt-2 text-[13px] font-medium text-ink-strong">Nothing selected</p>
        <p className="mt-1 max-w-[30ch] text-[12px] leading-relaxed text-muted">
          Click any block in the preview to edit its text, colors and options by hand.
        </p>
      </Card>
    );
  }
  return (
    <InspectorInner
      node={node}
      isRoot={node.node_id === rootNodeId}
      inLayoutParent={isLayoutParentName(parentName)}
      onChange={onChange}
      onMove={onMove}
      onRemove={onRemove}
      onDuplicate={onDuplicate}
      onUnwrap={onUnwrap}
    />
  );
}

function InspectorInner({
  node,
  isRoot,
  inLayoutParent,
  onChange,
  onMove,
  onRemove,
  onDuplicate,
  onUnwrap,
}: {
  node: ComponentNode;
  isRoot: boolean;
  inLayoutParent: boolean;
  onChange: (nodeId: string, prop: string, value: unknown) => void;
  onMove: (nodeId: string, delta: -1 | 1) => void;
  onRemove: (nodeId: string) => void;
  onDuplicate?: (nodeId: string) => void;
  onUnwrap?: (nodeId: string) => void;
}) {
  const entry = useMemo(
    () => (registry as Record<string, (typeof registry)[RegistryName] | undefined>)[node.component.name],
    [node.component.name],
  );
  const fields = useMemo(() => (entry ? describeSchema(entry.schema as unknown as SchemaLike) : []), [entry]);

  return (
    <Card className="p-4">
      <header className="mb-3 flex items-start justify-between gap-2 border-b border-hairline pb-3">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold tracking-tight text-ink-strong">{node.component.name}</p>
          <p className="truncate font-mono text-[10px] text-subtle">{node.node_id}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <IconBtn label="Move up" onClick={() => onMove(node.node_id, -1)} disabled={isRoot}>
            <ArrowUp className="h-4 w-4" strokeWidth={1.75} />
          </IconBtn>
          <IconBtn label="Move down" onClick={() => onMove(node.node_id, 1)} disabled={isRoot}>
            <ArrowDown className="h-4 w-4" strokeWidth={1.75} />
          </IconBtn>
          {onDuplicate && !isRoot && (
            <IconBtn label="Duplicate (⌘D)" onClick={() => onDuplicate(node.node_id)}>
              <Copy className="h-4 w-4" strokeWidth={1.75} />
            </IconBtn>
          )}
          {onUnwrap && !isRoot && entry?.acceptsChildren && (node.children?.length ?? 0) > 0 && (
            <IconBtn label="Ungroup (promote children)" onClick={() => onUnwrap(node.node_id)}>
              <Ungroup className="h-4 w-4" strokeWidth={1.75} />
            </IconBtn>
          )}
          <IconBtn label="Delete block" onClick={() => onRemove(node.node_id)} disabled={isRoot} danger>
            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
          </IconBtn>
        </div>
      </header>

      {!isRoot && (
        <SizeControls
          props={node.props}
          inLayoutParent={inLayoutParent}
          onChange={(prop, v) => onChange(node.node_id, prop, v)}
        />
      )}

      {!entry ? (
        <p className="text-[12px] text-accent">Component not in registry.</p>
      ) : fields.length === 0 ? (
        <p className="text-[12px] text-muted">This block has no editable properties.</p>
      ) : (
        <div className="space-y-3">
          {fields.map((f) => (
            <Field
              key={f.name}
              field={f}
              value={node.props[f.name]}
              componentName={node.component.name}
              onChange={(v) => onChange(node.node_id, f.name, v)}
            />
          ))}
        </div>
      )}

      {/* Form block: jump straight to this form's real submissions (#19). */}
      {node.component.name === "Form" && typeof node.props.formId === "string" && node.props.formId && (
        <div className="mt-3 border-t border-hairline pt-3">
          <FormSubmissionsButton formId={node.props.formId} />
        </div>
      )}
    </Card>
  );
}

const WIDTH_LABEL: Record<string, string> = {
  full: "Full width",
  wide: "Wide",
  medium: "Medium",
  narrow: "Narrow",
};
const ALIGN_LABEL: Record<string, string> = { left: "Left", center: "Center", right: "Right" };

type SizeBp = "base" | "mobile";
const RESP_KEYS = ["blockWidth", "blockAlign", "colSpan", "rowSpan"] as const;

/** Size & alignment: a generic block width + alignment (any block) plus grid
 *  colSpan/rowSpan when the block sits in a Grid/Columns. A Base/Mobile tab
 *  authors per-breakpoint overrides (#22) stored under props.responsive.mobile;
 *  TreeRenderer resolves them against the device toggle. */
function SizeControls({
  props,
  inLayoutParent,
  onChange,
}: {
  props: Record<string, unknown>;
  inLayoutParent: boolean;
  onChange: (prop: "colSpan" | "rowSpan" | "blockWidth" | "blockAlign" | "responsive", value: unknown) => void;
}) {
  const [bp, setBp] = useState<SizeBp>("base");
  const resp = props.responsive as Record<string, Record<string, unknown>> | undefined;
  const mob = resp?.mobile ?? {};
  const overrideCount = RESP_KEYS.filter((k) => k in mob).length;

  const cur = (v: unknown) => (typeof v === "number" ? String(v) : typeof v === "string" ? v : "");
  // Effective value at the active breakpoint (mobile falls back to base).
  const eff = (k: (typeof RESP_KEYS)[number]) => (bp === "mobile" && k in mob ? mob[k] : props[k]);
  const isOverridden = (k: (typeof RESP_KEYS)[number]) => bp === "mobile" && k in mob;

  const set = (k: (typeof RESP_KEYS)[number], value: unknown) => {
    if (bp === "base") {
      onChange(k, value);
      return;
    }
    const m: Record<string, unknown> = { ...mob };
    if (value === undefined) delete m[k];
    else m[k] = value;
    const next: Record<string, unknown> = { ...(resp ?? {}) };
    if (Object.keys(m).length) next.mobile = m;
    else delete next.mobile;
    onChange("responsive", Object.keys(next).length ? next : undefined);
  };
  const clearMobile = () => {
    const next: Record<string, unknown> = { ...(resp ?? {}) };
    delete next.mobile;
    onChange("responsive", Object.keys(next).length ? next : undefined);
  };

  const widthVal = cur(eff("blockWidth")) || "full";
  const constrained = widthVal !== "full";
  const dot = (k: (typeof RESP_KEYS)[number]) =>
    isOverridden(k) ? <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-accent align-middle" /> : null;

  return (
    <div className="mb-3 rounded-btn border border-hairline bg-paper-2/40 p-2.5 dark:bg-white/5">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[11px] font-medium text-ink">Size &amp; alignment</p>
        <div className="flex items-center gap-0.5 rounded-full bg-paper p-0.5 dark:bg-white/10">
          {(["base", "mobile"] as const).map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setBp(b)}
              className={cn(
                "rounded-full px-2 py-0.5 text-[10.5px] font-medium capitalize transition-colors",
                bp === b ? "bg-accent text-white" : "text-muted hover:text-ink",
              )}
            >
              {b}
              {b === "mobile" && overrideCount > 0 ? ` · ${overrideCount}` : ""}
            </button>
          ))}
        </div>
      </div>
      {bp === "mobile" && (
        <p className="mb-2 flex items-center justify-between text-[10px] text-subtle">
          <span>Overrides apply on phones; blank = inherit desktop.</span>
          {overrideCount > 0 && (
            <button type="button" onClick={clearMobile} className="text-accent hover:underline">
              Reset
            </button>
          )}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-0.5 block text-[10.5px] text-subtle">Width{dot("blockWidth")}</span>
          <select
            className={inputCls}
            value={widthVal}
            onChange={(e) => set("blockWidth", e.target.value === "full" ? undefined : e.target.value)}
          >
            {BLOCK_WIDTH_VALUES.map((v) => (
              <option key={v} value={v}>
                {WIDTH_LABEL[v] ?? v}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-0.5 block text-[10.5px] text-subtle">Align{dot("blockAlign")}</span>
          <select
            className={inputCls}
            value={cur(eff("blockAlign")) || "left"}
            disabled={!constrained}
            title={constrained ? undefined : "Set a width below full to align the block"}
            onChange={(e) => set("blockAlign", e.target.value === "left" ? undefined : e.target.value)}
          >
            {BLOCK_ALIGN_VALUES.map((v) => (
              <option key={v} value={v}>
                {ALIGN_LABEL[v] ?? v}
              </option>
            ))}
          </select>
        </label>
      </div>

      {inLayoutParent && (
        <div className="mt-2 grid grid-cols-2 gap-2 border-t border-hairline/60 pt-2">
          <label className="block">
            <span className="mb-0.5 block text-[10.5px] text-subtle">Columns wide{dot("colSpan")}</span>
            <select
              className={inputCls}
              value={cur(eff("colSpan"))}
              onChange={(e) => set("colSpan", e.target.value === "" ? undefined : e.target.value)}
            >
              <option value="">Auto</option>
              {COL_SPAN_VALUES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-0.5 block text-[10.5px] text-subtle">Rows tall{dot("rowSpan")}</span>
            <select
              className={inputCls}
              value={cur(eff("rowSpan"))}
              onChange={(e) => set("rowSpan", e.target.value === "" ? undefined : e.target.value)}
            >
              <option value="">Auto</option>
              {ROW_SPAN_VALUES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}

function IconBtn({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "grid h-7 w-7 place-items-center rounded-btn border border-hairline text-muted transition-colors disabled:opacity-30",
        danger ? "hover:border-accent/40 hover:text-accent" : "hover:border-ink/25 hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

const inputCls =
  "w-full rounded-btn border border-hairline bg-paper px-2.5 py-1.5 text-[13px] text-ink outline-none focus:border-accent/50 dark:bg-white/5";
const labelCls = "block text-[11px] font-medium text-ink";

/** Text props that benefit from AI copy rewriting (capability #2). */
const COPY_FIELDS = new Set([
  "headline", "subline", "eyebrow", "content", "heading", "title", "subtitle",
  "label", "ctaLabel", "quote", "question", "answer", "body", "tagline",
]);

function Field({
  field,
  value,
  componentName,
  onChange,
}: {
  field: FieldDescriptor;
  value: unknown;
  componentName?: string;
  onChange: (v: unknown) => void;
}) {
  const req = field.required ? <span className="text-accent"> *</span> : null;
  switch (field.kind.kind) {
    case "string": {
      const str = typeof value === "string" ? value : ((field.defaultValue as string | undefined) ?? "");
      // Raw-color prop (hex value or default) → native color picker + hex (#10).
      if (HEX_RE.test(str) || HEX_RE.test((field.defaultValue as string | undefined) ?? "")) {
        return (
          <label className="block space-y-1">
            <span className={labelCls}>
              {field.name}
              {req}
            </span>
            <ColorInput value={str} onChange={onChange} />
          </label>
        );
      }
      const canRewrite = (field.kind.multiline || COPY_FIELDS.has(field.name)) && str.trim().length > 0;
      return (
        <label className="block space-y-1">
          <span className={cn(labelCls, "flex items-center justify-between")}>
            <span>
              {field.name}
              {req}
            </span>
            {canRewrite && (
              <AiRewrite
                current={str}
                fieldLabel={componentName ? `${componentName} ${field.name}` : field.name}
                onPick={(v) => onChange(v)}
              />
            )}
          </span>
          {field.kind.multiline ? (
            <textarea className={inputCls} rows={3} value={str} onChange={(e) => onChange(e.target.value)} />
          ) : (
            <input type="text" className={inputCls} value={str} onChange={(e) => onChange(e.target.value)} />
          )}
        </label>
      );
    }
    case "number": {
      const num = typeof value === "number" ? value : ((field.defaultValue as number | undefined) ?? 0);
      return (
        <label className="block space-y-1">
          <span className={labelCls}>
            {field.name}
            {req}
          </span>
          <input
            type="number"
            className={inputCls}
            value={num}
            onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
          />
        </label>
      );
    }
    case "boolean": {
      const checked = typeof value === "boolean" ? value : ((field.defaultValue as boolean | undefined) ?? false);
      return (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-hairline accent-accent"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span className={labelCls}>{field.name}</span>
        </label>
      );
    }
    case "enum": {
      const v =
        typeof value === "string"
          ? value
          : ((field.defaultValue as string | undefined) ?? field.kind.options[0] ?? "");
      const opts = field.kind.options;
      const isColor = field.name === "background" || opts.every((o) => o in BG_SWATCH);
      const isSpacing = SPACING_FIELDS.has(field.name) && opts.length >= 3;
      return (
        <label className="block space-y-1">
          <span className={labelCls}>
            {field.name}
            {req}
          </span>
          {isColor ? (
            <ColorSwatches value={v} options={opts} onChange={onChange} />
          ) : isSpacing ? (
            <SpacingSlider value={v} options={opts} onChange={onChange} />
          ) : (
            <select className={inputCls} value={v} onChange={(e) => onChange(e.target.value)}>
              {opts.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          )}
        </label>
      );
    }
    case "json": {
      // Structured field builder for the Form block instead of raw JSON (#19).
      if (componentName === "Form" && field.name === "fields") {
        return <FormFieldsEditor value={value} onChange={onChange} />;
      }
      const text = value === undefined ? "" : safeStringify(value);
      return (
        <label className="block space-y-1">
          <span className={labelCls}>
            {field.name} <span className="text-subtle">(json)</span>
          </span>
          <textarea
            className={cn(inputCls, "font-mono text-[11px]")}
            rows={4}
            value={text}
            onChange={(e) => {
              const t = e.target.value;
              if (t.trim() === "") return onChange(undefined);
              try {
                onChange(JSON.parse(t));
              } catch {
                onChange(t);
              }
            }}
          />
        </label>
      );
    }
  }
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

/* --------------------------- visual prop editors (#10) -------------------- */

/** Theme-token color values → a CSS background for the swatch preview. Reads the
 *  live `--dl-*` vars so swatches reflect the active design. */
const BG_SWATCH: Record<string, string> = {
  transparent: "repeating-conic-gradient(#0000000d 0% 25%, transparent 0% 50%) 50% / 12px 12px",
  surface: "var(--dl-surface, #fafafa)",
  paper: "var(--dl-surface, #fafafa)",
  brand: "var(--dl-brand, #2563eb)",
  accent: "var(--dl-accent, #0ea5e9)",
  contrast: "var(--dl-contrast, #0a0a0a)",
  light: "var(--dl-surface, #fafafa)",
  dark: "var(--dl-contrast, #0a0a0a)",
};
const SPACING_FIELDS = new Set(["padding", "gap", "spacing", "margin"]);
const HEX_RE = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;

/** Clickable color swatches for token-typed color enums (e.g. `background`). */
function ColorSwatches({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const sel = o === value;
        return (
          <button
            key={o}
            type="button"
            title={o}
            onClick={() => onChange(o)}
            className={cn(
              "h-8 w-8 rounded-btn border transition-all",
              sel ? "border-accent ring-2 ring-accent/30" : "border-hairline hover:border-ink/30",
            )}
            style={{ background: BG_SWATCH[o] ?? "var(--dl-surface, #fafafa)" }}
            aria-label={o}
            aria-pressed={sel}
          />
        );
      })}
    </div>
  );
}

/** Segmented slider for ordinal spacing enums (none → xl) — drag or click. */
function SpacingSlider({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  const idx = Math.max(0, options.indexOf(value));
  return (
    <div>
      <input
        type="range"
        min={0}
        max={options.length - 1}
        step={1}
        value={idx}
        onChange={(e) => onChange(options[Number(e.target.value)] ?? options[0])}
        className="w-full accent-accent"
      />
      <div className="mt-0.5 flex justify-between">
        {options.map((o, i) => (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            className={cn(
              "text-[10px] tabular-nums transition-colors",
              i === idx ? "font-semibold text-accent" : "text-subtle hover:text-ink",
            )}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Native color picker + hex field for raw `#rrggbb` string props. */
function ColorInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const safe = HEX_RE.test(value) ? value : "#000000";
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={safe.length === 4 ? expandHex(safe) : safe}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-10 shrink-0 cursor-pointer rounded-btn border border-hairline bg-transparent p-0.5"
        aria-label="Pick color"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(inputCls, "font-mono text-[12px]")}
        placeholder="#000000"
      />
    </div>
  );
}

function expandHex(h: string): string {
  // #abc → #aabbcc (native color input needs 6 digits).
  return "#" + h.slice(1).split("").map((c) => c + c).join("");
}

/** Inline "rewrite with AI" affordance: asks ai-agent for alternative copy and
 *  shows a small picker. Selecting a variant applies it to the field. (#2) */
const TONES = ["punchy", "reassuring", "premium", "playful", "plain"] as const;
export function AiRewrite({
  current,
  fieldLabel,
  onPick,
}: {
  current: string;
  fieldLabel: string;
  onPick: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tone, setTone] = useState<(typeof TONES)[number]>("punchy");
  const [variants, setVariants] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function fetchVariants(nextTone = tone) {
    setLoading(true);
    setError(null);
    try {
      const r = await aiAssist.copy({ current, field_label: fieldLabel, tone: nextTone, n: 4 });
      if (r.ok && r.variants?.length) setVariants(r.variants);
      else setError(r.error || "No suggestions");
    } catch {
      setError("Couldn’t reach the AI service");
    } finally {
      setLoading(false);
    }
  }

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        title="Rewrite with AI"
        onClick={(e) => {
          e.preventDefault();
          const next = !open;
          setOpen(next);
          if (next && variants.length === 0) void fetchVariants();
        }}
        className="inline-flex items-center gap-1 rounded-btn border border-hairline px-1.5 py-0.5 text-[10px] font-medium text-accent transition-colors hover:bg-accent-soft"
      >
        <Sparkles className="h-3 w-3" strokeWidth={2} />
        AI
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div
            className="absolute right-0 top-6 z-40 w-72 rounded-card border border-hairline bg-paper p-2 shadow-lg dark:bg-ink-2"
            onClick={(e) => e.preventDefault()}
          >
            <div className="mb-1.5 flex items-center gap-1 overflow-x-auto pb-0.5">
              {TONES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setTone(t);
                    void fetchVariants(t);
                  }}
                  className={cn(
                    "shrink-0 rounded-full px-2 py-0.5 text-[10.5px] capitalize transition-colors",
                    t === tone ? "bg-accent text-white" : "bg-paper-2 text-muted hover:text-ink dark:bg-white/5",
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
            {loading ? (
              <div className="flex items-center gap-2 px-1 py-3 text-[12px] text-muted">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Writing options…
              </div>
            ) : error ? (
              <p className="px-1 py-2 text-[12px] text-accent">{error}</p>
            ) : (
              <div className="space-y-1">
                {variants.map((v, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      onPick(v);
                      setOpen(false);
                    }}
                    className="block w-full rounded-btn px-2 py-1.5 text-left text-[12.5px] leading-snug text-ink transition-colors hover:bg-accent-soft"
                  >
                    {v}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}

/* ------------------------- component library browser (#14) ---------------- */

/** A lucide glyph per component, giving the browser a visual cue without
 *  rendering live (prop-hungry) previews. Falls back to a generic box. */
const BLOCK_ICON: Record<string, LucideIcon> = {
  Header: PanelTop,
  Footer: PanelBottom,
  Hero: Megaphone,
  Section: RectangleHorizontal,
  Container: Box,
  Grid: LayoutGrid,
  Columns: Columns3,
  MeshGradientBg: Palette,
  Prose: AlignLeft,
  Text: Type,
  Image: ImageIcon,
  Video: PlaySquare,
  CTAButton: MousePointerClick,
  Form: ClipboardList,
  Stats: BarChart3,
  FAQ: HelpCircle,
  Testimonials: Quote,
  Pricing: Tags,
  LogoBar: GalleryHorizontalEnd,
};

export function AddBlockMenu({ onAdd }: { onAdd: (name: string) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const groups = useMemo(() => {
    const reg = registry as Record<string, { description?: string; acceptsChildren?: boolean } | undefined>;
    const needle = q.trim().toLowerCase();
    const match = (name: string) =>
      !needle || name.toLowerCase().includes(needle) || (reg[name]?.description ?? "").toLowerCase().includes(needle);
    const containers: string[] = [];
    const content: string[] = [];
    for (const name of COMPONENT_NAMES) {
      if (!match(name)) continue;
      (reg[name]?.acceptsChildren ? containers : content).push(name);
    }
    return { containers, content, reg };
  }, [q]);

  const add = (name: string) => {
    onAdd(name);
    setOpen(false);
    setQ("");
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-btn border border-hairline px-2.5 py-1.5 text-[12px] font-medium text-muted transition-colors hover:border-ink/25 hover:text-ink"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={2} />
        Add block
        <ChevronDown className="h-3 w-3" strokeWidth={2} />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ink/30 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden />
          <div className="relative flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-card border border-hairline bg-paper shadow-2xl dark:bg-ink-2">
            <div className="border-b border-hairline p-3">
              <div className="flex items-center gap-2 rounded-btn border border-hairline bg-paper-2 px-2.5 dark:bg-white/5">
                <Search className="h-4 w-4 shrink-0 text-subtle" strokeWidth={1.75} />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search blocks…"
                  className="w-full bg-transparent py-2 text-[14px] text-ink outline-none placeholder:text-subtle"
                />
              </div>
            </div>
            <div className="overflow-y-auto p-3">
              {groups.content.length === 0 && groups.containers.length === 0 ? (
                <p className="px-1 py-8 text-center text-[13px] text-muted">No blocks match “{q}”.</p>
              ) : (
                <>
                  <BlockGroup title="Content blocks" names={groups.content} reg={groups.reg} onAdd={add} />
                  <BlockGroup title="Layout & containers" names={groups.containers} reg={groups.reg} onAdd={add} />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function BlockGroup({
  title,
  names,
  reg,
  onAdd,
}: {
  title: string;
  names: string[];
  reg: Record<string, { description?: string; acceptsChildren?: boolean } | undefined>;
  onAdd: (name: string) => void;
}) {
  if (names.length === 0) return null;
  return (
    <div className="mb-4 last:mb-0">
      <p className="eyebrow px-1 pb-1.5">{title}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {names.map((name) => {
          const Icon = BLOCK_ICON[name] ?? Box;
          return (
            <button
              key={name}
              type="button"
              onClick={() => onAdd(name)}
              className="group flex flex-col gap-1.5 rounded-card border border-hairline bg-paper-2/40 p-3 text-left transition-colors hover:border-accent/40 hover:bg-accent-soft/40 dark:bg-white/5"
            >
              <span className="grid h-8 w-8 place-items-center rounded-btn bg-paper text-accent dark:bg-white/10">
                <Icon className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <span className="text-[13px] font-medium text-ink-strong">{name}</span>
              <span className="line-clamp-2 text-[11px] leading-snug text-subtle">
                {reg[name]?.description ?? ""}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
